import { EUserRole } from '@/app/[locale]/(dashboard)/dashboard/users/domain/enums/users.enum';
import type { IRoleNavigationRules } from '../interfaces/navigation.interface';

/**
 * Configuración de reglas de navegación por rol
 * Define qué rutas puede acceder cada tipo de usuario
 */
export const ROLE_NAVIGATION_RULES: Partial<Record<EUserRole, IRoleNavigationRules>> = {
  [EUserRole.OWNER]: {
    role: EUserRole.OWNER,
    defaultRedirect: '/dashboard/analytics',
    allowedRoutes: [
      '/dashboard',
      '/dashboard/analytics',
      '/dashboard/logs',
      '/dashboard/documents',
      '/dashboard/support',
      '/dashboard/pending-signatures',
      '/dashboard/users',
      '/dashboard/user-signatures',
      '/dashboard/settings',
      '/dashboard/dev-tools',
      '/dashboard/admin-config',
      '/dashboard/api-keys',
      '/dashboard/plans-packs',
      '/dashboard/payments',
      '/dashboard/transactions',
      '/dashboard/store',
      '/dashboard/my-subscription',
      '/dashboard/my-profile',
      '/dashboard/bulk-download',
      '/dashboard/checkout',
    ],
    forbiddenRoutes: [],
    navigationMenu: [
      {
        path: '/dashboard/analytics',
        title: 'Dashboard',
        icon: 'LayoutDashboard',
      },
      {
        path: '/dashboard/logs',
        title: 'Logs',
        icon: 'FileText',
      },
      {
        path: '/dashboard/documents',
        title: 'Documentos',
        icon: 'FileText',
      },
      {
        path: '/dashboard/pending-signatures',
        title: 'Por firmar',
        icon: 'PenLine',
      },
      {
        path: '/dashboard/users',
        title: 'Usuarios',
        icon: 'Users',
      },
      {
        path: '/dashboard/support',
        title: 'Soporte',
        icon: 'MessageCircle',
      },
      {
        path: '/dashboard/user-signatures',
        title: 'Firmas',
        icon: 'FileSignature',
      },
      {
        path: '/dashboard/plans-packs',
        title: 'Planes y Packs',
        icon: 'CreditCard',
      },
      {
        path: '/dashboard/payments',
        title: 'Pagos',
        icon: 'Receipt',
      },
      {
        path: '/dashboard/transactions',
        title: 'Transacciones',
        icon: 'ClipboardList',
      },
      {
        path: '/dashboard/api-keys',
        title: 'API Keys',
        icon: 'Key',
      },
      {
        path: '/dashboard/dev-tools',
        title: 'Dev Tools',
        icon: 'Wrench',
      },
      {
        path: '/dashboard/admin-config',
        title: 'Configuración',
        icon: 'Settings2',
      },
    ],
  },

  [EUserRole.SUPER_ADMIN]: {
    role: EUserRole.SUPER_ADMIN,
    defaultRedirect: '/dashboard/analytics',
    allowedRoutes: [
      '/dashboard',
      '/dashboard/analytics',
      '/dashboard/logs',
      '/dashboard/documents',
      '/dashboard/support',
      '/dashboard/pending-signatures',
      '/dashboard/users',
      '/dashboard/user-signatures',
      '/dashboard/settings',
      '/dashboard/plans-packs',
      '/dashboard/payments',
      '/dashboard/transactions',
      '/dashboard/store',
      '/dashboard/my-subscription',
      '/dashboard/my-profile',
      '/dashboard/bulk-download',
      '/dashboard/checkout',
    ],
    forbiddenRoutes: [],
    navigationMenu: [
      {
        path: '/dashboard/analytics',
        title: 'Dashboard',
        icon: 'LayoutDashboard',
      },
      {
        path: '/dashboard/logs',
        title: 'Logs',
        icon: 'FileText',
      },
      {
        path: '/dashboard/documents',
        title: 'Documentos',
        icon: 'FileText',
      },
      {
        path: '/dashboard/pending-signatures',
        title: 'Por firmar',
        icon: 'PenLine',
      },
      {
        path: '/dashboard/users',
        title: 'Usuarios',
        icon: 'Users',
      },
      {
        path: '/dashboard/support',
        title: 'Soporte',
        icon: 'MessageCircle',
      },
      {
        path: '/dashboard/user-signatures',
        title: 'Firmas',
        icon: 'FileSignature',
      },
      {
        path: '/dashboard/plans-packs',
        title: 'Planes y Packs',
        icon: 'CreditCard',
      },
      {
        path: '/dashboard/payments',
        title: 'Pagos',
        icon: 'Receipt',
      },
      {
        path: '/dashboard/transactions',
        title: 'Transacciones',
        icon: 'ClipboardList',
      },
    ],
  },

  // SUPER_ADMIN_AUDITOR - Auditor global: solo lectura, sin secciones de escritura pura
  [EUserRole.SUPER_ADMIN_AUDITOR]: {
    role: EUserRole.SUPER_ADMIN_AUDITOR,
    defaultRedirect: '/dashboard/analytics',
    allowedRoutes: [
      '/dashboard',
      '/dashboard/analytics',
      '/dashboard/documents',
      '/dashboard/support',
      '/dashboard/users',
      '/dashboard/payments',
    ],
    forbiddenRoutes: [
      '/dashboard/pending-signatures',
      '/dashboard/templates',
      '/dashboard/user-signatures',
      '/dashboard/plans-packs',
      '/dashboard/organizations',
      '/dashboard/logs',
    ],
    navigationMenu: [
      {
        path: '/dashboard/analytics',
        title: 'Dashboard',
        icon: 'LayoutDashboard',
      },
      {
        path: '/dashboard/documents',
        title: 'Documentos',
        icon: 'FileText',
      },
      {
        path: '/dashboard/users',
        title: 'Usuarios',
        icon: 'Users',
      },
      {
        path: '/dashboard/payments',
        title: 'Pagos',
        icon: 'Receipt',
      },
      {
        path: '/dashboard/support',
        title: 'Soporte',
        icon: 'MessageCircle',
      },
    ],
  },


  [EUserRole.ADMIN_ORGANIZATION]: {
    role: EUserRole.ADMIN_ORGANIZATION,
    defaultRedirect: '/dashboard/analytics',
    allowedRoutes: [
      '/dashboard',
      '/dashboard/analytics',
      '/dashboard/documents',
      '/dashboard/support',
      '/dashboard/pending-signatures',
      '/dashboard/my-transactions',
      '/dashboard/bulk-send',
      '/dashboard/users',
      '/dashboard/settings',
      '/dashboard/store',
      '/dashboard/my-subscription',
      '/dashboard/my-profile',
      '/dashboard/bulk-download',
      '/dashboard/checkout',
    ],
    forbiddenRoutes: ['/dashboard/user-signatures'],
    navigationMenu: [
      {
        path: '/dashboard/analytics',
        title: 'Dashboard',
        icon: 'LayoutDashboard',
      },
      {
        path: '/dashboard/documents',
        title: 'Documentos',
        icon: 'FileText',
      },
      {
        path: '/dashboard/pending-signatures',
        title: 'Por firmar',
        icon: 'PenLine',
      },
      {
        path: '/dashboard/my-transactions',
        title: 'Mis Set Documentales',
        icon: 'Folders',
      },
      {
        path: '/dashboard/users',
        title: 'Usuarios',
        icon: 'Users',
      },
      {
        path: '/dashboard/support',
        title: 'Soporte',
        icon: 'MessageCircle',
      },
    ],
  },

  [EUserRole.USER_ORGANIZATION]: {
    role: EUserRole.USER_ORGANIZATION,
    defaultRedirect: '/dashboard/documents',
    allowedRoutes: [
      '/dashboard',
      '/dashboard/documents',
      '/dashboard/support',
      '/dashboard/pending-signatures',
      '/dashboard/my-transactions',
      '/dashboard/bulk-send',
      '/dashboard/settings',
      '/dashboard/store',
      '/dashboard/my-subscription',
      '/dashboard/my-profile',
      '/dashboard/bulk-download',
      '/dashboard/checkout',
    ],
    forbiddenRoutes: ['/dashboard/users', '/dashboard/user-signatures'],
    navigationMenu: [
      {
        path: '/dashboard',
        title: 'Dashboard',
        icon: 'LayoutDashboard',
      },
      {
        path: '/dashboard/documents',
        title: 'Documentos',
        icon: 'FileText',
      },
      {
        path: '/dashboard/pending-signatures',
        title: 'Por firmar',
        icon: 'PenLine',
      },
      {
        path: '/dashboard/my-transactions',
        title: 'Mis Set Documentales',
        icon: 'Folders',
      },
      {
        path: '/dashboard/support',
        title: 'Soporte',
        icon: 'MessageCircle',
      },
    ],
  },

  [EUserRole.AUDITOR_ORGANIZATION]: {
    role: EUserRole.AUDITOR_ORGANIZATION,
    defaultRedirect: '/dashboard/documents',
    allowedRoutes: ['/dashboard', '/dashboard/documents', '/dashboard/support', '/dashboard/pending-signatures', '/dashboard/settings'],
    forbiddenRoutes: ['/dashboard/users', '/dashboard/user-signatures'],
    navigationMenu: [
      {
        path: '/dashboard',
        title: 'Dashboard',
        icon: 'LayoutDashboard',
      },
      {
        path: '/dashboard/documents',
        title: 'Documentos',
        icon: 'FileText',
      },
      {
        path: '/dashboard/pending-signatures',
        title: 'Por firmar',
        icon: 'PenLine',
      },
      {
        path: '/dashboard/support',
        title: 'Soporte',
        icon: 'MessageCircle',
      },
    ],
  },

  [EUserRole.EXECUTIVE_ORGANIZATION]: {
    role: EUserRole.EXECUTIVE_ORGANIZATION,
    defaultRedirect: '/dashboard/documents',
    allowedRoutes: [
      '/dashboard',
      '/dashboard/documents',
      '/dashboard/support',
      '/dashboard/pending-signatures',
      '/dashboard/settings',
      '/dashboard/store',
      '/dashboard/my-subscription',
      '/dashboard/my-profile',
      '/dashboard/bulk-download',
      '/dashboard/checkout',
    ],
    forbiddenRoutes: ['/dashboard/users', '/dashboard/user-signatures'],
    navigationMenu: [
      {
        path: '/dashboard',
        title: 'Dashboard',
        icon: 'LayoutDashboard',
      },
      {
        path: '/dashboard/documents',
        title: 'Documentos',
        icon: 'FileText',
      },
      {
        path: '/dashboard/pending-signatures',
        title: 'Por firmar',
        icon: 'PenLine',
      },
      {
        path: '/dashboard/support',
        title: 'Soporte',
        icon: 'MessageCircle',
      },
    ],
  },

  [EUserRole.CLIENT]: {
    role: EUserRole.CLIENT,
    defaultRedirect: '/dashboard/documents',
    allowedRoutes: [
      '/dashboard',
      '/dashboard/documents',
      '/dashboard/support',
      '/dashboard/pending-signatures',
      '/dashboard/my-transactions',
      '/dashboard/bulk-send',
      '/dashboard/templates',
      '/dashboard/settings',
      '/dashboard/store',
      '/dashboard/my-subscription',
      '/dashboard/my-profile',
      '/dashboard/bulk-download',
      '/dashboard/checkout',
    ],
    forbiddenRoutes: ['/dashboard/users', '/dashboard/user-signatures'],
    navigationMenu: [
      {
        path: '/dashboard',
        title: 'Dashboard',
        icon: 'LayoutDashboard',
      },
      {
        path: '/dashboard/documents',
        title: 'Documentos',
        icon: 'FileText',
      },
      {
        path: '/dashboard/pending-signatures',
        title: 'Por firmar',
        icon: 'PenLine',
      },
      {
        path: '/dashboard/my-transactions',
        title: 'Mis Set Documentales',
        icon: 'Folders',
      },
      {
        path: '/dashboard/templates',
        title: 'Plantillas',
        icon: 'FileStack',
      },
      {
        path: '/dashboard/support',
        title: 'Soporte',
        icon: 'MessageCircle',
      },
    ],
  },
};
